<template>
  <div>
    <div v-if="loading" class="text-center py-8 text-gray-300">Loading...</div>
    <div v-else-if="error" class="text-center py-8 text-red-400">{{ error }}</div>

    <template v-else-if="session">
      <div class="break-inside rounded-md bg-gray-600 p-4 text-center mb-4">
        <div class="mb-2">
          <RouterLink :to="`/astronomers/${session.astronomer.slug}`">
            <img
              v-if="session.astronomer.avatar_url"
              :src="session.astronomer.avatar_url"
              :title="session.astronomer.username"
              class="w-10 rounded-full mx-auto"
            />
            <span v-else class="text-sm">{{ session.astronomer.username }}</span>
          </RouterLink>
        </div>

        <RouterLink :to="`/astronomers/${session.astronomer.slug}/targets/${session.target.id}`">
          <h3 class="text-yellow-300" :title="session.target.name">
            {{ session.target.name }}
          </h3>
        </RouterLink>

        <ImageStatsTable :stats="session.image_statistics" />

        <h5 class="mt-4">Last Image</h5>
        <img
          v-if="session.last_image_thumbnail"
          class="mx-auto"
          :src="`data:image/jpeg;base64, ${session.last_image_thumbnail}`"
        />

        <span class="text-xs" :title="session.session_ended_at">
          {{ timeAgo(session.session_ended_at) }} ago
          <template v-if="session.last_image_rms && session.last_image_rms > 0">
            | RMS: {{ session.last_image_rms.toFixed(2) }}"
          </template>
        </span>
      </div>

      <div
        v-for="(dataset, statType) in session.graph_data"
        :key="statType"
        class="mb-6"
      >
        <canvas :ref="(el) => registerCanvas(el, statType)" class="session-statistic-graph" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import Chart from 'chart.js/auto'
import ImageStatsTable from '../components/ImageStatsTable.vue'
import { timeAgo } from '../utils/timeAgo'

Chart.defaults.color = '#fff'

interface ImageStat {
  filter: string | null
  duration: number
  count: number
}

interface GraphDataset {
  label: string
  backgroundColor: string
  borderColor: string
  data: { x: number; y: number }[]
}

interface Session {
  id: number
  camera_name: string | null
  telescope_name: string | null
  session_started_at: string | null
  session_ended_at: string
  last_image_thumbnail: string | null
  last_image_rms: number | null
  can_monitor: boolean
  image_statistics: ImageStat[]
  graph_data: Record<string, GraphDataset>
  astronomer: { id: number; username: string; avatar_url: string | null; slug: string }
  target: { id: number; name: string }
}

const route = useRoute()

const session = ref<Session | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const charts: Record<string, Chart> = {}
const canvasRefs: Record<string, HTMLCanvasElement> = {}

function registerCanvas(el: unknown, statType: string) {
  if (el instanceof HTMLCanvasElement) {
    canvasRefs[statType] = el
  }
}

function buildCharts() {
  if (!session.value) return

  for (const [statType, dataset] of Object.entries(session.value.graph_data)) {
    const canvas = canvasRefs[statType]
    if (!canvas) continue

    if (charts[statType]) {
      charts[statType].data.datasets[0].data = dataset.data
      charts[statType].update()
    } else {
      charts[statType] = new Chart(canvas, {
        type: 'line',
        data: { datasets: [dataset] },
        options: {
          scales: { x: { type: 'linear', ticks: { stepSize: 1 } } },
          plugins: { title: { display: true, text: dataset.label } },
        },
      })
    }
  }
}

function destroyCharts() {
  for (const chart of Object.values(charts)) chart.destroy()
  Object.keys(charts).forEach((k) => delete charts[k])
}

async function fetchSession() {
  loading.value = true
  error.value = null

  try {
    const response = await fetch(`/api/spa/sessions/${route.params.id}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
    if (!response.ok) throw new Error(`Request failed: ${response.status}`)
    session.value = await response.json()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load session'
  } finally {
    loading.value = false
  }
}

async function reload() {
  if (!session.value) return

  try {
    const response = await fetch(`/api/spa/sessions/${route.params.id}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
    if (!response.ok) return
    session.value = await response.json()
    buildCharts()
  } catch {
    // silent — keep showing stale data
  }
}

let pollTimer: ReturnType<typeof setTimeout> | null = null

function schedulePoll() {
  pollTimer = setTimeout(async () => {
    await reload()
    schedulePoll()
  }, 30_000)
}

watch(session, (newVal) => {
  if (newVal) {
    // defer to next tick so canvas refs are rendered
    setTimeout(buildCharts, 0)
  }
})

onMounted(async () => {
  await fetchSession()
  schedulePoll()
})

onUnmounted(() => {
  if (pollTimer !== null) clearTimeout(pollTimer)
  destroyCharts()
})
</script>
